import { createStore, applyMiddleware, combineReducers } from 'redux';
import { HYDRATE, createWrapper } from 'next-redux-wrapper';
import thunkMiddleware from 'redux-thunk';

import { reducer as retailersReducer } from 'src/modules/retailer'; //, RetailerActions
import { reducer as brandSpacesReducer } from 'src/modules/brandSpaces'; //, RetailerActions
import { reducer as designersReducer } from 'src/modules/designers';
import { reducer as productsReducer } from 'src/modules/products';
import pagesReducer from 'src/modules/pages/store/reducers';
import headerReducer from 'src/modules/layout/store/reducers';
import { reducer as professionalsReducer } from 'src/modules/professionals';
import { reducer as localeReducer } from 'src/modules/locale';
import { reducer as cookiesReducer } from 'src/modules/cookies';
import { reducer as articlesReducer } from 'src/modules/articles';
import { reducer as searchReducer } from 'src/modules/search';
import { reducer as eventsReducer } from 'src/modules/events';
import { reducer as jobsReducer } from 'src/modules/jobs';

export * from './types';

// Redux devtools crashes when payload or state are too big to serialise. It happens with retailer.
// https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/Troubleshooting.md#excessive-use-of-memory-and-cpu
// Serializing the action
const actionSanitizer = (action) => {
  //action.type === RetailerActions.SetMarkers action.type.startsWith('RETAILER/')
  return action.type.startsWith('RETAILER/') && action.payload
    ? { ...action, payload: '<<LONG_BLOB>>' }
    : action;
};

const bindMiddleware = (middleware) => {
  // if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { composeWithDevTools } = require('redux-devtools-extension');

  // Sanitizing the state
  const composeEnhancers = composeWithDevTools({
    actionSanitizer,
  });
  return composeEnhancers(applyMiddleware(...middleware));
};

const combinedReducer = combineReducers({
  pages: pagesReducer,
  retail: retailersReducer,
  brandSpaces: brandSpacesReducer,
  designers: designersReducer,
  products: productsReducer,
  header: headerReducer,
  professionals: professionalsReducer,
  locale: localeReducer,
  cookies: cookiesReducer,
  articles: articlesReducer,
  search: searchReducer,
  events: eventsReducer,
  jobs: jobsReducer,
});

// state = client state
const reducer = (state, action) => {
  if (action.type === HYDRATE) {
    //eslint:disable:no-unused-variable
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { retail: serverStateRetail, ...serverState } = action.payload;

    const nextState = {
      ...state, // use previous state
      ...serverState, // apply delta from hydration
      header: state.header,
      pages: {
        ...serverState.pages,
        // TODO: error was happening here
        pages: { ...state?.pages?.pages, ...serverState.pages.pages },
      },
      locale: {
        ...serverState.locale,
        locale: state.locale.locale,
      },
    };

    // Use server state first time, after that use current state
    if (state?.retail?.retailers?.length < 1 && serverStateRetail?.retailers?.length > 0) {
      nextState.retail = serverStateRetail;
    }

    if (!nextState?.articles?.listing?.articleCategories?.length) {
      if (state?.articles?.listing?.articleCategories?.length) {
        nextState.articles.listing.articleCategories = state.articles.listing.articleCategories;
      } else if (serverState?.articles?.listing?.articleCategories?.length) {
        nextState.articles.listing.articleCategories =
          serverState.articles.listing.articleCategories;
      }
    }

    // When changing locale on PLP, the product category will become null in the server state. Keeping it from the client state
    if (state.products.listing.isLoaded) {
      // Slug from the Product category loaded on the server side
      const serverCategorySlug = serverState.products.listing.productCategory?.slug;
      if (serverCategorySlug) {
        //Check if the product category is stored in the loaded listing store
        if (state.products.listing.loadedListing[serverCategorySlug]) {
          nextState.products.listing = {
            ...state.products.listing.loadedListing[serverCategorySlug],
            loadedListing: state.products.listing.loadedListing,
          };
          state.products.listing.productCategory;
        }
        // Otherwise replace the client state with the server state, but keep existing loadedListing
        else {
          nextState.products.listing = {
            ...serverState.products.listing,
            loadedListing: state.products.listing.loadedListing,
          };
        }
      } else {
        // Keep the existing product listing store
        nextState.products.listing = state.products.listing;
      }
    }

    // Keep product categories
    if (state.products.categories.productCategories.length) {
      nextState.products.categories = state.products.categories;
    }

    // Always keep articles from the client state except for the first SSR load for the hero article
    // console.log(serverState.articles.listing.article);
    // if (serverState.articles.listing.article) {
    //   nextState.articles.listing = {
    //     ...state.articles.listing,
    //     article: serverState.articles.listing.article,
    //   };
    // } else {
    //   nextState.articles.listing = state.articles.listing;
    // }

    // Always keep references from the client state
    nextState.professionals.references.listing = state.professionals.references.listing;
    nextState.articles.instagrams = state.articles.instagrams;
    nextState.cookies.enabledCookieGroups = state.cookies.enabledCookieGroups;

    if (state.count) nextState.count = state.count; // preserve count value on client side navigation

    return nextState;
  } else {
    return combinedReducer(state, action);
  }
};

export const initStore = () => {
  return createStore(reducer, bindMiddleware([thunkMiddleware]));
};

export const wrapper = createWrapper(initStore);
